import {
  ActionIcon, Anchor, Button, Group, Select, Stack, Switch, TextInput, Textarea, Tooltip,
} from '@mantine/core'
import { IconPlus, IconX } from '@tabler/icons-react'
import { useState } from 'react'
import { useForm } from '@mantine/form'
import { zod4Resolver as zodResolver } from 'mantine-form-zod-resolver'
import { TIPOS_SUMINISTRO, proveedorEntrada, type Proveedor, type ProveedorEntrada } from '@serkat/shared'

interface Props {
  inicial?: Proveedor
  guardando: boolean
  onGuardar: (datos: ProveedorEntrada) => void
  onCancelar: () => void
}

/**
 * El esquema Zod es el MISMO que valida en el API. Definir que un RUC son 11
 * dígitos con prefijo válido se hace una vez, en packages/shared.
 */
export function FormularioProveedor({ inicial, guardando, onGuardar, onCancelar }: Props) {
  // Solo dos de cada seis proveedores dan una segunda dirección, así que el
  // campo se pide, no se ofrece de entrada. Pero si el proveedor YA tiene una,
  // se muestra desplegado: esconder un dato existente es peor que el desorden.
  const [correoAlterno, setCorreoAlterno] = useState(Boolean(inicial?.correoAlterno))
  const [cuentaUsd, setCuentaUsd] = useState(
    Boolean(inicial?.cuentaCorrienteUsd ?? inicial?.cuentaCciUsd),
  )

  const form = useForm<ProveedorEntrada>({
    initialValues: {
      codigo: inicial?.codigo ?? '',
      razonSocial: inicial?.razonSocial ?? '',
      ruc: inicial?.ruc ?? '',
      domicilioFiscal: inicial?.domicilioFiscal ?? '',
      contactoNombre: inicial?.contactoNombre ?? '',
      telefono: inicial?.telefono ?? '',
      correo: inicial?.correo ?? '',
      correoAlterno: inicial?.correoAlterno ?? '',
      rubro: inicial?.rubro ?? '',
      descripcion: inicial?.descripcion ?? '',
      tipoSuministro: inicial?.tipoSuministro ?? null,
      formaPagoId: inicial?.formaPagoId ?? null,
      cuentaCorriente: inicial?.cuentaCorriente ?? '',
      cuentaCci: inicial?.cuentaCci ?? '',
      cuentaCorrienteUsd: inicial?.cuentaCorrienteUsd ?? '',
      cuentaCciUsd: inicial?.cuentaCciUsd ?? '',
      cuentaDetraccion: inicial?.cuentaDetraccion ?? '',
      observaciones: inicial?.observaciones ?? '',
      activo: inicial?.activo ?? true,
    },
    validate: zodResolver(proveedorEntrada),
  })

  return (
    <form onSubmit={form.onSubmit(onGuardar)}>
      <Stack gap="sm">
        <TextInput
          label="Razón social"
          withAsterisk
          {...form.getInputProps('razonSocial')}
        />
        <Group grow align="flex-start">
          <TextInput
            label="RUC"
            withAsterisk
            maxLength={11}
            placeholder="20481234567"
            {...form.getInputProps('ruc')}
          />
          <TextInput label="Código" placeholder="PROV-0001" {...form.getInputProps('codigo')} />
        </Group>

        <TextInput label="Domicilio fiscal" {...form.getInputProps('domicilioFiscal')} />

        <Group grow align="flex-start">
          <TextInput label="Contacto" {...form.getInputProps('contactoNombre')} />
          <TextInput label="Teléfono" {...form.getInputProps('telefono')} />
        </Group>

        <Group grow align="flex-start">
          <Stack gap={4}>
            <TextInput label="Correo" {...form.getInputProps('correo')} />
            {!correoAlterno && (
              <Anchor
                component="button"
                type="button"
                size="xs"
                onClick={() => setCorreoAlterno(true)}
              >
                <Group gap={4} wrap="nowrap">
                  <IconPlus size={12} />
                  Agregar otro correo
                </Group>
              </Anchor>
            )}
          </Stack>
          <Select
            label="Provee"
            data={TIPOS_SUMINISTRO.map((t) => ({
              value: t,
              label: t === 'AMBOS' ? 'Producto y servicio' : t.toLowerCase(),
            }))}
            clearable
            {...form.getInputProps('tipoSuministro')}
          />
        </Group>

        {correoAlterno && (
          <TextInput
            label="Correo alterno"
            description="Algunos proveedores atienden en dos direcciones"
            rightSection={
              <Tooltip label="Quitar">
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={() => {
                    // Se limpia al ocultarlo: un campo invisible con contenido
                    // se guardaría sin que nadie lo vea.
                    form.setFieldValue('correoAlterno', '')
                    setCorreoAlterno(false)
                  }}
                >
                  <IconX size={14} />
                </ActionIcon>
              </Tooltip>
            }
            {...form.getInputProps('correoAlterno')}
          />
        )}

        <TextInput label="Rubro" {...form.getInputProps('rubro')} />

        <Stack gap={4}>
          <Group grow align="flex-start">
            <TextInput
              label={cuentaUsd ? 'Cuenta corriente (soles)' : 'Cuenta corriente'}
              {...form.getInputProps('cuentaCorriente')}
            />
            <TextInput
              label={cuentaUsd ? 'CCI (soles)' : 'CCI'}
              {...form.getInputProps('cuentaCci')}
            />
          </Group>
          {!cuentaUsd && (
            <Anchor component="button" type="button" size="xs"
              onClick={() => setCuentaUsd(true)}>
              <Group gap={4} wrap="nowrap">
                <IconPlus size={12} />
                Agregar cuenta en dólares
              </Group>
            </Anchor>
          )}
        </Stack>

        {cuentaUsd && (
          <Group grow align="flex-start">
            <TextInput
              label="Cuenta corriente (dólares)"
              description="Se imprime cuando la orden es en dólares"
              {...form.getInputProps('cuentaCorrienteUsd')}
            />
            <TextInput
              label="CCI (dólares)"
              rightSection={
                <Tooltip label="Quitar">
                  <ActionIcon variant="subtle" size="sm"
                    onClick={() => {
                      // Se limpian al ocultarlas: unos campos invisibles con
                      // contenido se guardarían sin que nadie los vea.
                      form.setFieldValue('cuentaCorrienteUsd', '')
                      form.setFieldValue('cuentaCciUsd', '')
                      setCuentaUsd(false)
                    }}>
                    <IconX size={14} />
                  </ActionIcon>
                </Tooltip>
              }
              {...form.getInputProps('cuentaCciUsd')}
            />
          </Group>
        )}
        <TextInput
          label="Cuenta de detracción"
          description="Se imprime en la orden cuando la operación está sujeta al SPOT"
          {...form.getInputProps('cuentaDetraccion')}
        />

        <Textarea label="Observaciones" autosize minRows={2} {...form.getInputProps('observaciones')} />

        <Switch
          label="Proveedor activo"
          {...form.getInputProps('activo', { type: 'checkbox' })}
        />

        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={onCancelar}>
            Cancelar
          </Button>
          <Button type="submit" loading={guardando}>
            Guardar
          </Button>
        </Group>
      </Stack>
    </form>
  )
}
